import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, boardingPdfUrl, formatErr } from "@/lib/api";
import { money } from "@/lib/brands";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Save, Home, Download, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { CloseFlow } from "@/components/CloseFlow";

export default function BoardingForm() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const patientId = sp.get("patient");
  const [bid, setBid] = useState(id || null);
  const [patient, setPatient] = useState(null);
  const [rec, setRec] = useState({ boarding_type: "day", check_in: "", check_out: "", notes: "" });
  const [status, setStatus] = useState("open");
  const [amountPaid, setAmountPaid] = useState(null);
  const [busy, setBusy] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [amount, setAmount] = useState("");

  useEffect(() => {
    (async () => {
      if (id) {
        const { data } = await api.get(`/boarding/${id}`);
        setRec({ boarding_type: data.boarding_type, check_in: data.check_in || "", check_out: data.check_out || "", notes: data.notes || "" });
        setStatus(data.status); setAmountPaid(data.amount_paid);
        const { data: p } = await api.get(`/patients/${data.patient_id}`); setPatient(p);
      } else if (patientId) {
        const { data: p } = await api.get(`/patients/${patientId}`); setPatient(p);
      }
    })();
  }, [id, patientId]);

  const save = async () => {
    if (!patient) return;
    setBusy(true);
    try {
      if (bid) {
        await api.put(`/boarding/${bid}`, { patient_id: patient.id, ...rec });
        toast.success("Boarding saved");
      } else {
        const { data } = await api.post("/boarding", { patient_id: patient.id, ...rec });
        setBid(data.id); toast.success("Boarding created");
        navigate(`/boarding/${data.id}/edit`, { replace: true });
      }
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmClose = async (amt) => {
    const { data } = await api.post(`/boarding/${bid}/close`, { amount: amt });
    setStatus("closed"); setAmountPaid(data.amount_paid);
    return bid;
  };
  const scheduleNext = async (dateISO) => {
    await api.post("/service-reminders", { record_type: "boarding", record_id: bid, due_date: dateISO });
  };

  if (!patient) return <div className="p-8">Loading…</div>;

  return (
    <div className="p-4 md:p-8 max-w-[760px] mx-auto animate-fade-in">
      <button onClick={() => navigate(-1)} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-3"><ArrowLeft className="h-4 w-4" /> Back</button>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2"><Home className="h-5 w-5 text-primary" /><h1 className="font-primary text-3xl font-bold tracking-tight">Boarding</h1>
            <span data-testid="boarding-status" className={`text-xs px-2 py-0.5 rounded-full ${status === "closed" ? "bg-emerald-500/15 text-emerald-600" : "bg-amber-500/15 text-amber-600"}`}>{status === "closed" ? "Closed" : "Open"}</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{patient.pet_name} · {patient.owner_name} · {patient.case_number} · {patient.owning_branch}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={save} disabled={busy} variant="outline" data-testid="save-boarding-btn"><Save className="h-4 w-4 mr-2" />Save</Button>
          {bid && <a href={boardingPdfUrl(bid)} target="_blank" rel="noreferrer"><Button variant="outline" data-testid="boarding-pdf-btn"><Download className="h-4 w-4 mr-2" />PDF</Button></a>}
          <Button onClick={() => { setAmount(amountPaid ?? ""); setCloseOpen(true); }} disabled={busy || !bid} data-testid="close-boarding-btn"><CheckCircle2 className="h-4 w-4 mr-2" />{status === "closed" ? "Edit Amount" : "Close Record"}</Button>
        </div>
      </div>
      {status === "closed" && <Card className="p-4 mb-4 border-emerald-500/30 bg-emerald-500/5 text-sm">Amount paid: <span className="font-semibold">{money(amountPaid)}</span></Card>}

      <Card className="p-6 border-border/60 space-y-5">
        <div>
          <Label>Boarding type</Label>
          <RadioGroup value={rec.boarding_type} onValueChange={(v) => setRec((r) => ({ ...r, boarding_type: v }))} className="flex gap-6 mt-2">
            <div className="flex items-center gap-2"><RadioGroupItem value="day" id="bt-day" data-testid="boarding-type-day" /><Label htmlFor="bt-day" className="cursor-pointer">Day Boarding</Label></div>
            <div className="flex items-center gap-2"><RadioGroupItem value="overnight" id="bt-night" data-testid="boarding-type-overnight" /><Label htmlFor="bt-night" className="cursor-pointer">Overnight / Multi-day</Label></div>
          </RadioGroup>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Check-in</Label><Input type="datetime-local" value={rec.check_in} onChange={(e) => setRec((r) => ({ ...r, check_in: e.target.value }))} data-testid="boarding-checkin" /></div>
          <div className="space-y-1.5"><Label>Check-out</Label><Input type="datetime-local" value={rec.check_out} onChange={(e) => setRec((r) => ({ ...r, check_out: e.target.value }))} data-testid="boarding-checkout" /></div>
        </div>
        <div className="space-y-1.5"><Label>Notes (feeding, meds, belongings, special instructions)</Label><Textarea rows={4} value={rec.notes} onChange={(e) => setRec((r) => ({ ...r, notes: e.target.value }))} data-testid="boarding-notes" /></div>
      </Card>

      <CloseFlow
        open={closeOpen}
        onOpenChange={setCloseOpen}
        title={status === "closed" ? "Update boarding amount" : "Close boarding"}
        initialAmount={amountPaid ?? ""}
        onConfirmClose={confirmClose}
        pdfUrl={boardingPdfUrl}
        onSchedule={scheduleNext}
        scheduleLabel="next boarding session"
      />
    </div>
  );
}
